import { styled } from '@mui/material/styles';
import { NavLink, useLocation } from 'react-router-dom';
import { Typography } from "@mui/material";
import MuiDrawer, { drawerClasses } from '@mui/material/Drawer';
import Box from '@mui/material/Box';
import List from '@mui/material/List';
import ListItem from '@mui/material/ListItem';
import ListItemButton from '@mui/material/ListItemButton';
import ListItemIcon from '@mui/material/ListItemIcon';
import ListItemText from '@mui/material/ListItemText';
import Stack from '@mui/material/Stack';
import React from 'react';
// import Tooltip from '@mui/material/Tooltip'; // Tooltip removed
import LockIcon from '@mui/icons-material/Lock'; // Added LockIcon
import { useOrganization, useAuth } from '@clerk/clerk-react';

import CallSplitIcon from '@mui/icons-material/CallSplit';
import FormatListBulletedIcon from '@mui/icons-material/FormatListBulleted';
import SearchIcon from '@mui/icons-material/Search';
import ResearchIcon from '@mui/icons-material/Explore';
import AccountTreeIcon from '@mui/icons-material/AccountTree';
import BackupTableIcon from '@mui/icons-material/BackupTable';
import DescriptionIcon from '@mui/icons-material/Description';
import ChatIcon from '@mui/icons-material/Chat';
import PrecisionManufacturingIcon from '@mui/icons-material/PrecisionManufacturing';
import IdeaDropdown from './IdeaDropdown';

const drawerWidth = 225;

const Drawer = styled(MuiDrawer)(({ theme }) => ({
    width: drawerWidth,
    flexShrink: 0,
    boxSizing: 'border-box',
    [`& .${drawerClasses.paper}`]: {
        width: drawerWidth,
        boxSizing: 'border-box',
        backgroundColor: theme.palette.background.paper,
    },
}));

export default function Sidebar({ open, handleDrawerClose }) {

    const renderVersion = () => {
        return 'version 1.0.3'
    }

    const { organization } = useOrganization();
    const { has } = useAuth(); // Use the useAuth hook to get the 'has' function

    // Determine if the organization has full access
    const hasFullAccess = organization?.publicMetadata?.full_access === "true" || has({ feature: 'full_access' });
    const location = useLocation();

    // Only show Project item if REACT_APP_SHOW_PROJECTS env var is set to true
    // This flag is here because it is a hard-coded, unimplemented feature.
    const showProjects = process.env.REACT_APP_SHOW_PROJECTS === 'true';
    
    const listItems = [
        { text: 'Keywords', icon: <SearchIcon />, path: '/Keywords' },
        { text: 'Research', icon: <ResearchIcon />, path: '/Research' },
        ...(showProjects ? [{ text: 'Project', icon: <BackupTableIcon />, path: '/Project' }] : []),
        { text: 'Summary', icon: <FormatListBulletedIcon />, path: '/Summary' },
        { text: 'Tracer', icon: <AccountTreeIcon />, path: '/Tracer-Requirements' },
        { text: 'Documents', icon: <DescriptionIcon />, path: '/Documents/dhf' },
        { text: 'Predicates', icon: <PrecisionManufacturingIcon />, path: '/Predicates' },
        { text: 'Decision Tree', icon: <CallSplitIcon sx={{ transform: 'rotate(180deg)' }} />, path: '/DecisionTree' },
        { text: 'Chatbot', icon: <ChatIcon />, path: '/Chatbot' },
    ];

    const isPathActive = (path) => location.pathname.startsWith(path);

    const ListItemContent = ({ item, isActive, requiresFullAccessButNotGranted }) => (
        // 'disabled' prop removed from ListItemButton to make it clickable
        // Visual 'disabled' state (opacity) will be handled if requiresFullAccessButNotGranted is true
        <ListItemButton
            selected={isActive}
            // disabled={disabled} // Removed: we want the button to be clickable to go to /Subscribe
            sx={{
                opacity: requiresFullAccessButNotGranted ? 0.6 : 1, // Dim if requires full access but not granted
                cursor: 'pointer', // Indicate it's clickable
                borderRadius: theme => theme.shape.borderRadius,
                '&.Mui-selected': {
                    backgroundColor: '#daede7',
                    '&:hover': {
                        backgroundColor: '#C0E2D6'
                    }
                },
                // Style for disabled state
                '&.Mui-disabled': {
                    opacity: 0.5,
                    cursor: 'not-allowed',
                }
            }}
        >
            <ListItemIcon sx={{
                minWidth: 20, // Reverted minWidth as lock icon is now outside
                paddingLeft: 1.5,
                '& .MuiSvgIcon-root': { 
                    fontSize: '1.25rem' 
                }
            }}>
                {item.icon}
            </ListItemIcon>
            <ListItemText 
                primary={item.text} 
                primaryTypographyProps={{ 
                    fontSize: '0.83rem', 
                    fontWeight: isActive ? 'bold' : 500,
                    fontFamily: 'Inter, sans-serif',
                    paddingLeft: 1.5,
                }} 
            />
            {requiresFullAccessButNotGranted && 
                <LockIcon sx={{ 
                    fontSize: '1rem', 
                    marginLeft: 'auto', // Push to the right
                    color: 'gold' // Make it yellow
                }} />
            }
        </ListItemButton>
    );

    // Render the Idea item with the dropdown
    const renderIdeaItem = () => {
        return (
            // Make the ListItem the relative container
            <ListItem sx={{ display: 'flex', justifyContent: 'space-between', position: 'relative' }}> 
                {/* Left side - NavLink */}
                <Box sx={{ flexGrow: 1,  }}> 
                    <ListItemText 
                        primary="Idea:"
                        primaryTypographyProps={{
                            fontSize: '0.83rem', 
                            fontWeight: 500,
                            fontFamily: 'Inter, sans-serif'
                        }} 
                    />
                </Box>

                {/* Absolutely positioned IdeaDropdown on the right */}
                <Box sx={{
                    position: 'absolute',
                    right: '5px',
                    top: '50%',
                    transform: 'translateY(-50%)',
                    zIndex: 1 
                }}
                onClick={(e) => {
                    // Prevent click from bubbling up to NavLink/ListItem
                    e.stopPropagation();
                }}
                >
                    <IdeaDropdown /> 
                </Box>
            </ListItem>
        );
    };

    return (
        <Drawer variant="permanent">
            <Box
                sx={{
                    mt: '4em',
                    height: '100%',
                    display: 'flex',
                    flexDirection: 'column',
                    marginTop: '3em',
                    backgroundColor: '#f5f6fa'
                }}
            >
                <Stack sx={{ flexGrow: 1, pt: 2, px: 1 }}>
                    <List>
                        {renderIdeaItem()}
                        {listItems.map((item) => (
                            <ListItem 
                                key={item.text} 
                                disablePadding 
                            >
                                {item.text === 'Idea' 
                                    ? renderIdeaItem(item)
                                    : (
                                        // Normal handling for other items
                                        (() => {
                                            // console.log('Rendering sidebar item:', item.text, 'Path:', item.path, 'Has full access:', hasFullAccess);

                                            const isDisabled = item.text !== 'Keywords' && !hasFullAccess;
                                            // Pass 'requiresFullAccessButNotGranted' instead of 'disabled' to ListItemContent
                                            let renderedItem = (
                                                <ListItemContent
                                                    item={item}
                                                    isActive={item.path ? isPathActive(item.path) : false}
                                                    requiresFullAccessButNotGranted={isDisabled}
                                                />
                                            );

                                            // console.log('Is disabled:', isDisabled);
                                            // console.log("Item.path:", item.path);

                                            // If it's disabled (requires full access but user doesn't have it), link to /Subscribe
                                            // Otherwise, if it has a path and is NOT disabled, link to its original path
                                            if (isDisabled) {
                                                renderedItem = (
                                                    <NavLink
                                                        to="/Subscribe" // Link to Subscribe page
                                                        style={{
                                                            textDecoration: 'none',
                                                            width: '100%',
                                                            color: 'inherit',
                                                        }}
                                                    >
                                                        {renderedItem}
                                                    </NavLink>
                                                );
                                            } else if (item.path) { // Link to original path if not disabled
                                                renderedItem = (
                                                    <NavLink
                                                        to={item.path}
                                                        style={{
                                                            textDecoration: 'none',
                                                            width: '100%',
                                                            color: 'inherit',
                                                        }}
                                                    >
                                                        {renderedItem}
                                                    </NavLink>
                                                );
                                            }
                                            
                                            // Tooltip and conditional div wrapper are removed as links are now always active (either to feature or to subscribe)


                                            return renderedItem;
                                        })()
                                    )
                                }
                            </ListItem>
                        ))}
                    </List>
                </Stack>

                {/* Bottom section pinned with mt:auto */}
                <Box sx={{ mt: 'auto', p: 2, textAlign: 'center' }}>
                    <Typography 
                        variant="caption" 
                        sx={{ color: '#018B57' }}
                    >
                        {renderVersion()}
                    </Typography>
                </Box>
            </Box>
        </Drawer>
    );
}
